import type { FC, ReactNode } from 'react'

import { useRef } from 'react'

import { gsap, useGsap, prefersReducedMotion } from '@/lib/gsap'

/* The index stays typographic, which is right for a studio's writing, but the
   pictures come back as an interaction rather than as eight stock covers
   stacked down the page: one frame follows the cursor and swaps to whichever
   row is under it. Pointer-driven only, so touch and keyboard never see it and
   the layout never has to make room for it. */
export const RowPreview: FC = (): ReactNode => {
    const scope = useRef<HTMLDivElement>(null)

    useGsap(() => {
        const frame = scope.current

        if (!frame || prefersReducedMotion()) {
            return
        }

        if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
            return
        }

        const list = frame.parentElement

        if (!list) {
            return
        }

        const picture = frame.querySelector<HTMLImageElement>('[data-preview-image]')

        if (!picture || list.querySelectorAll('[data-preview-src]').length === 0) {
            return
        }

        gsap.set(frame, { xPercent: -50, yPercent: -50, opacity: 0, scale: 0.86 })

        const moveX = gsap.quickTo(frame, 'x', { duration: 0.65, ease: 'power3.out' })
        const moveY = gsap.quickTo(frame, 'y', { duration: 0.65, ease: 'power3.out' })
        const tiltY = gsap.quickTo(frame, 'rotate', { duration: 0.8, ease: 'power3.out' })

        let last = 0
        let visible = false

        const show = (src: string): void => {
            if (picture.getAttribute('src') !== src) {
                picture.setAttribute('src', src)
            }

            if (visible) {
                return
            }

            visible = true
            gsap.to(frame, { opacity: 1, scale: 1, duration: 0.55, ease: 'noma-soft' })
        }

        const hide = (): void => {
            if (!visible) {
                return
            }

            visible = false
            gsap.to(frame, { opacity: 0, scale: 0.86, duration: 0.45, ease: 'noma-soft' })
            tiltY(0)
        }

        /* Resolved from whatever is actually under the cursor rather than from
           per-row enter and leave listeners. A wheel scroll moves the rows out
           from under a stationary pointer without firing `pointerleave`, which
           is what left the frame stranded over a later section. */
        const onMove = (event: PointerEvent): void => {
            moveX(event.clientX)
            moveY(event.clientY)

            const drift = gsap.utils.clamp(-7, 7, (event.clientX - last) * 0.5)

            tiltY(drift)
            last = event.clientX

            const target = event.target as Element | null
            const row = target?.closest?.('[data-preview-src]') as HTMLElement | null
            const src = row?.dataset.previewSrc

            if (src && list.contains(row)) {
                show(src)

                return
            }

            hide()
        }

        window.addEventListener('pointermove', onMove)
        window.addEventListener('scroll', hide, { passive: true })
        window.addEventListener('blur', hide)
        document.addEventListener('pointerleave', hide)

        return () => {
            window.removeEventListener('pointermove', onMove)
            window.removeEventListener('scroll', hide)
            window.removeEventListener('blur', hide)
            document.removeEventListener('pointerleave', hide)
        }
    }, scope, [])

    return (
        <div
            ref={scope}
            aria-hidden='true'
            className='pointer-events-none fixed left-0 top-0 z-[60] hidden h-[230px] w-[300px] overflow-hidden rounded-[var(--radius-frame)] bg-ink-soft opacity-0 shadow-[0_24px_60px_-20px_rgba(5,5,5,0.55)] lg:block'
        >
            <img
                data-preview-image
                alt=''
                className='size-full object-cover'
            />
        </div>
    )
}
